<div class="d-flex mx-1">
  <div
    class="tasks d-flex flex-column flex-shrink-0 position-relative bg-light bg-opacity-10"
    cdkDrag
    cdkDragLockAxis="x"
    [cdkDragData]="stage"
    (mouseenter)="onStagePointerEnter()"
    (mouseleave)="onStagePointerLeave()"
  >
    <div
      class="task-header d-flex justify-content-between sticky position-relative bg-primary-lighten border-bottom"
    >
      <div class="position-absolute top-0 start-50 translate-middle-x w-100">
        <div
          class="cursor-pointer rounded-top stage-color-trigger"
          style="height: 10px"
          [style.backgroundColor]="stage.bgColor"
          (click)="onToggleColorPicker($event)"
        ></div>

        @if (activeStageColorPicker === stage._id) {
          <div class="stage-color-popover card shadow-sm border-0 rounded">
            <div class="p-1" (click)="$event.stopPropagation()">
              <div class="stage-color-grid d-flex flex-wrap gap-1">
                @for (color of stageColorPalette; track color) {
                  <button
                    type="button"
                    class="stage-color-option btn p-0"
                    [class.active]="stage.bgColor === color"
                    [style.backgroundColor]="color"
                    (click)="onChangeColor(color)"
                  ></button>
                }
              </div>
            </div>
          </div>
        }
      </div>

      <div class="flex-grow-1 me-2">
        @if (isEditing) {
          <input
            #stageNameInput
            type="text"
            class="stage-title-input m-0"
            [(ngModel)]="stageNameDraft"
            (keydown.enter)="onNameEnter($any($event))"
            (keydown.escape)="onNameEscape($any($event))"
            (blur)="onCancelEdit()"
            autofocus
          />
        } @else {
          <div class="d-flex flex-column align-items-start">
            <h5 class="mt-1 mb-0">
              <span class="editable-stage-title" (click)="onStartEdit($event)">
                {{ stage.name }} ({{ stageCount ?? cardPager.total ?? 0 }})
                @if (stage.wonDeal === true) {
                  <span class="badge bg-success rounded-pill font-12"
                    >{{ 'kanban.stageColumn.won' | translate }}</span
                  >
                }
                @if (stage.lostDeal === true) {
                  <span class="badge bg-danger rounded-pill font-12"
                    >{{ 'kanban.stageColumn.lost' | translate }}</span
                  >
                }
                @if (stage.kind) {
                  <!-- The stage's purpose in the funnel (E2 RF-14), orthogonal to won/lost. -->
                  <span class="badge bg-soft-primary text-primary rounded-pill font-12"
                    >{{ stageKindLabel(stage.kind) | translate }}</span
                  >
                }
              </span>
              <!-- What the filters took from this column: the count it has without them. -->
              @if (hidesDeals) {
                <span
                  class="badge bg-soft-secondary text-secondary font-11 ms-1"
                  [matTooltip]="'kanban.stageColumn.ofBaseTooltip' | translate"
                  >{{ 'kanban.stageColumn.ofBase' | translate: { count: stageBaseCount } }}</span
                >
              }
            </h5>
            <div class="mt-1">
              <span
                class="d-block fw-semibold font-14 ms-1"
                [ngClass]="{
                  'text-success': stage.wonDeal === true,
                  'text-danger': stage.lostDeal === true,
                }"
              >
                {{
                  stage.wonDeal === true
                    ? "+"
                    : stage.lostDeal === true
                      ? "-"
                      : ""
                }}
                <!--
                  The stage total (RF-03), from the same query the cards below come
                  from. Two numbers used to sit in this header: this line summed only
                  the loaded page, and a second one came from a hardcoded-period
                  summary with no filters — so a stage reading (0) could show a value.
                  Falls back to the page sum only while the totals are in flight.
                -->
                {{
                  ((stageValue ?? getStageTotalAmount())
                    | currency: "BRL" : "symbol-narrow" : "1.2-2") ?? "R$ 0,00"
                }}
              </span>
            </div>
          </div>
        }
      </div>

      <button
        class="stage-drag-handle btn px-3 py-2 me-1 d-flex align-items-center justify-content-center w-100"
        cdkDragHandle
      ></button>

      <div
        class="dropdown dropdown-content float-end"
        (click)="$event.stopPropagation()"
      >
        <a
          class="dropdown-toggle text-muted arrow-none"
          data-bs-toggle="dropdown"
        >
          <i class="mdi mdi-dots-vertical font-16"></i>
        </a>

        <div class="dropdown-menu dropdown-menu-end">
          @if (acceptsNewCards) {
            <a
              href="javascript:void(0);"
              class="dropdown-item"
              (click)="openModalCard(stage)"
            >
              {{ 'kanban.stageColumn.newOpportunity' | translate }}
            </a>
          }
          <button
            href="javascript:void(0);"
            class="dropdown-item"
            [disabled]="isStageWon()"
            (click)="onUpdateStatus('won')"
          >
            {{ 'kanban.stageColumn.stageWon' | translate }}
          </button>
          <button
            href="javascript:void(0);"
            class="dropdown-item"
            [disabled]="isStageLost()"
            (click)="onUpdateStatus('lost')"
          >
            {{ 'kanban.stageColumn.stageLost' | translate }}
          </button>
          <!-- Stage purpose (E2 RF-14): rules anchor to it, never to the name. -->
          <div class="dropdown-divider"></div>
          <h6 class="dropdown-header">{{ 'kanban.stageColumn.kindHeader' | translate }}</h6>
          @for (option of stageKinds; track option.value) {
            <button
              class="dropdown-item d-flex justify-content-between align-items-center"
              (click)="onUpdateKind(option.value)"
            >
              {{ option.label | translate }}
              @if (stage.kind === option.value) {
                <i class="mdi mdi-check text-primary"></i>
              }
            </button>
          }
          @if (stage.kind) {
            <button class="dropdown-item" (click)="onUpdateKind(null)">
              {{ 'kanban.stageColumn.kindClear' | translate }}
            </button>
          }
          @if (hasStageStatus()) {
            <a
              href="javascript:void(0);"
              class="dropdown-item"
              (click)="onUpdateStatus('reset')"
            >
              {{
                isStageWon()
                  ? ('kanban.stageColumn.revertStageWon' | translate)
                  : ('kanban.stageColumn.revertStageLost' | translate)
              }}
            </a>
          }
          <a
            href="javascript:void(0);"
            class="dropdown-item text-danger"
            (click)="onDeleteStage()"
          >
            {{ 'kanban.stageColumn.removeStage' | translate }}
          </a>
        </div>
      </div>
    </div>

    <div
      class="task-list-items flex-grow-1 scroll"
      cdkDropList
      [id]="stage._id"
      [cdkDropListData]="cardPager.list"
      [cdkDropListConnectedTo]="connectedDropLists"
      (cdkDropListDropped)="onCardDropped($any($event))"
      (scroll)="onScroll($event)"
    >
      <div class="position-relative py-2 mx-0">
        @if (cardPager.list && cardPager.total > 0) {
          @for (card of cardPager.list; track $index) {
            <!--
              `card-wrapper` is the class this component's SCSS already writes for:
              `cursor: grab`, and `overflow: hidden` ONLY while the list is being
              dragged. It was never applied to any element, so both rules were dead and
              the clipping had been inlined here as `style="overflow: hidden"` —
              permanent instead of drag-scoped.

              Permanent clipping means every dropdown inside a card is cut at the card's
              own edge. It went unnoticed while the card was nine stacked rows tall
              (the temperature menu still fitted INSIDE that box); once the card was
              recomposed into four bands there was no direction left where the menu fits,
              and it came out sliced.

              Inline styles are prohibited anyway (.claude/rules/11-web-ui-hyper.md).
            -->
            <div
              class="card-wrapper"
              cdkDrag
              [cdkDragData]="card"
              (cdkDragStarted)="onCardDragStarted($event, card)"
              (cdkDragMoved)="onCardDragMoved()"
              (cdkDragEnded)="onCardDragEnded()"
            >
              <app-card-item
                [card]="card"
                (cardClick)="openDetailCard(card)"
                (editCard)="openModalCard(stage, card)"
                (deleteCard)="deleteCard(card)"
                [stage]="stage"
                (scheduleNextAction)="openNextActionDialog($event)"
                (openScoreExplanation)="openScoreExplanation($event)"
                (concludeNextAction)="openConcludeDialog($event)"
              />
            </div>
          }
        }
        @if (emptiedByFilters) {
          <div class="stage-empty-note text-center text-muted font-12 p-2 mx-1">
            {{ 'kanban.stageColumn.emptyFiltered' | translate }}<br />
            <small>{{ 'kanban.stageColumn.emptyFilteredBase' | translate: { count: stageBaseCount } }}</small>
          </div>
        }
        @if (acceptsNewCards) {
          <button
            type="button"
            class="btn btn-outline-light fw-bold w-100 position-absolute top-100 start-0 end-0 mt-0"
            [class.d-none]="hoveredStageId !== stage._id"
            (click)="openModalCard(stage)"
          >
            <i class="mdi mdi-plus me-1"></i>
            {{ 'kanban.stageColumn.newOpportunity' | translate }}
          </button>
        }
      </div>
    </div>
  </div>
</div>
